import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InventoryService } from '../services/inventoryService';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import styles from '../styles/step8ListModal.module.css';
import { loadLocalizedContent } from '../i18n/documents';

interface Step9ListModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  onListUpdate?: () => void;
}

type TimingOption = 'Ready Now' | 'Soon' | 'Future' | 'Never' | '';
type FormOption = 'In Person' | 'Phone/Zoom' | 'Email/Letter' | 'Unsent Letter' | '';

/** Stored option values stay English; only the display label is localized. */
const TIMING_OPTIONS: { value: TimingOption; labelKey: string }[] = [
  { value: 'Ready Now', labelKey: 'step.9.list.timing.ready_now' },
  { value: 'Soon', labelKey: 'step.9.list.timing.soon' },
  { value: 'Future', labelKey: 'step.9.list.timing.future' },
  { value: 'Never', labelKey: 'step.9.list.timing.never' },
];

const FORM_OPTIONS: { value: FormOption; labelKey: string }[] = [
  { value: 'In Person', labelKey: 'step.9.list.form.in_person' },
  { value: 'Phone/Zoom', labelKey: 'step.9.list.form.phone_zoom' },
  { value: 'Email/Letter', labelKey: 'step.9.list.form.email_letter' },
  { value: 'Unsent Letter', labelKey: 'step.9.list.form.unsent_letter' },
];

interface PersonItem {
  name: string;
  note?: string;
  timing?: TimingOption;
  form?: FormOption;
  amendsPlan?: string;
  amendsCompleted?: boolean;
  amendsExperience?: string;
}

const Step9ListModal: React.FC<Step9ListModalProps> = ({ isOpen, onClose, user, onListUpdate }) => {
  const { showConfirm, showAlert, showHelp } = useDialog();
  const { t } = useLocale();
  const [persons, setPersons] = useState<PersonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [form, setForm] = useState<PersonItem | null>(null);
  const [entryInput, setEntryInput] = useState('');
  
  // Auto-save state management
  const [savingEntry, setSavingEntry] = useState<number | null>(null);
  const [savedEntry, setSavedEntry] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { handleError } = useAuthErrorHandler();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const periodicSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const INVENTORY_TYPE = 'step9-persons';
  const VERSION = 'default';

  // Check if a person entry has amends completed (checkbox checked)
  const isPersonCompleted = (person: PersonItem): boolean => {
    if (!person || typeof person !== 'object') return false;
    return person.amendsCompleted === true;
  };

  // Check if a person entry has an amends plan
  const hasAmendsPlan = (person: PersonItem): boolean => {
    if (!person || typeof person !== 'object') return false;
    const plan = (person.amendsPlan && typeof person.amendsPlan === 'string') ? person.amendsPlan.trim() : '';
    return plan.length > 0;
  };

  // Load list when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadList();
    } else if (isOpen && !user) {
      setLoading(false);
      setPersons([]);
    }
  }, [isOpen, user]);

  const loadList = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const items = await InventoryService.getInventory(INVENTORY_TYPE, VERSION);
      // Filter out completion status items and any undefined/null items
      const personItems = items
        .filter(item => item != null && typeof item === 'object' && item._type !== 'completion_status')
        .map(item => ({
          name: item.name || '',
          note: item.note || '',
          timing: (item.timing as TimingOption) || undefined,
          form: (item.form as FormOption) || undefined,
          amendsPlan: item.amendsPlan || '',
          amendsCompleted: item.amendsCompleted || false,
          amendsExperience: item.amendsExperience || ''
        }))
        .filter(item => item != null); // Final safety check
      setPersons(personItems);
    } catch (error) {
      // If no list exists yet, start with empty array
      setPersons([]);
    } finally {
      setLoading(false);
    }
  };

  // Copy from Step 8
  const handleCopyFromStep8 = async () => {
    try {
      // Check if Step 9 already has persons
      if (persons.length > 0) {
        showAlert({
          message: t('step.9.list.dialog.copy_existing_entries'),
          variant: 'warning'
        });
        return;
      }

      // Load persons from Step 8
      const step8Items = await InventoryService.getInventory('step8-persons', 'default');
      const step8Persons = step8Items
        .filter(item => item != null && typeof item === 'object' && item._type !== 'completion_status')
        .map(item => ({
          name: item.name || '',
          note: item.note || '',
          timing: (item.timing as TimingOption) || undefined,
          form: (item.form as FormOption) || undefined,
          amendsPlan: '',
          amendsCompleted: false,
          amendsExperience: ''
        }));

      if (step8Persons.length === 0) {
        showAlert({
          message: t('step.9.list.dialog.copy_none_found'),
          variant: 'info'
        });
        return;
      }

      // Save to Step 9 inventory
      await InventoryService.saveInventory(INVENTORY_TYPE, VERSION, step8Persons);
      setPersons(step8Persons);

      showAlert({
        message: t('step.9.list.dialog.copy_success', { count: step8Persons.length }),
        variant: 'success'
      });

      // Notify parent to update counters
      if (onListUpdate) {
        onListUpdate();
      }
    } catch (error) {
      console.error('Error copying from Step 8:', error);
      showAlert({
        message: t('step.9.list.dialog.copy_failed'),
        variant: 'danger'
      });
    }
  };

  const getCurrentData = () => {
    return persons;
  };

  // Immediate save function (bypasses debounce) - used for critical operations
  const immediateSaveEntry = useCallback(async (idx: number, formData: PersonItem): Promise<boolean> => {
    const currentData = getCurrentData();
    
    // Validate index
    if (idx < 0 || idx >= currentData.length) {
      console.error('Invalid index for save:', idx, 'array length:', currentData.length);
      return false;
    }
    
    // Check if data actually changed
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentData[idx]);
    if (!hasChanges) return true;

      try {
        const newData = [...currentData];
        newData[idx] = formData;
        
        // Filter out any undefined/null items and ensure all items are valid objects
        const validData = newData.filter(item => item != null && typeof item === 'object');

        await InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData);

        // Update local state
        setPersons(validData);

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);

      // Notify parent to update counters
      if (onListUpdate) {
        onListUpdate();
      }

      return true;
    } catch (error: any) {
      console.error('Error saving entry:', error);
      
      // Check if it's an authentication error
      const errorMessage = error?.message || String(error);
      const isAuthError = errorMessage.includes('401') || 
                         errorMessage.includes('Not logged in') ||
                         errorMessage.includes('Token expired') ||
                         errorMessage.includes('Invalid token') ||
                         errorMessage.includes('No authentication token');
      
      if (isAuthError) {
        showAlert({
          title: t('step.9.list.dialog.session_expired_title'),
          message: t('step.9.list.dialog.session_expired_message'),
          okText: t('step.9.list.dialog.ok')
        });
      }
      
      return false;
    }
  }, [onListUpdate, t]);

  // Auto-save function with debouncing
  const autoSaveEntry = useCallback(async (idx: number, formData: PersonItem) => {
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      const currentData = getCurrentData();
      
      // Validate index
      if (idx < 0 || idx >= currentData.length) {
        console.error('Invalid index for auto-save:', idx, 'array length:', currentData.length);
        setHasUnsavedChanges(false);
        return;
      }
      
      // Check if data actually changed
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentData[idx]);
      if (!hasChanges) {
        setHasUnsavedChanges(false);
        return;
      }

      // Set saving state
      setSavingEntry(idx);
      setSaveError(null);
      setSavedEntry(null);

      try {
        const newData = [...currentData];
        newData[idx] = formData;
        
        // Filter out any undefined/null items and ensure all items are valid objects
        const validData = newData.filter(item => item != null && typeof item === 'object');

        await InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData);

        // Update local state
        setPersons(validData);

        // Show success feedback
        setSavingEntry(null);
        setSavedEntry(idx);
        setTimeout(() => setSavedEntry(null), 1000);

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);

        // Notify parent to update counters
        if (onListUpdate) {
          onListUpdate();
        }
      } catch (error: any) {
        console.error('Error auto-saving entry:', error);
        setSavingEntry(null);
        setSaveError(idx);
        
        // Check if it's an authentication error
        const errorMessage = error?.message || String(error);
        const isAuthError = errorMessage.includes('401') || 
                           errorMessage.includes('Not logged in') ||
                           errorMessage.includes('Token expired') ||
                           errorMessage.includes('Invalid token') ||
                           errorMessage.includes('No authentication token');
        
        if (isAuthError) {
          showAlert({
            title: t('step.9.list.dialog.session_expired_title'),
            message: t('step.9.list.dialog.session_expired_message'),
            okText: t('step.9.list.dialog.ok')
          });
        }
      }
    }, 300);
  }, [onListUpdate, t]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, []);

  // Periodic auto-save (every 10 seconds when entry is expanded)
  useEffect(() => {
    // Clear any existing interval
    if (periodicSaveIntervalRef.current) {
      clearInterval(periodicSaveIntervalRef.current);
    }

    // Only start periodic save if we have an expanded entry
    if (expandedIndex !== null && form && user) {
      periodicSaveIntervalRef.current = setInterval(() => {
        autoSaveEntry(expandedIndex, form);
      }, 10000); // 10 seconds
    }

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, [expandedIndex, form, user, autoSaveEntry]);

  // Save on visibility change (when user switches browser tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && expandedIndex !== null && form) {
        autoSaveEntry(expandedIndex, form);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expandedIndex, form, autoSaveEntry]);

  const openHelp = async (fieldId: string) => {
    try {
      const text = await loadLocalizedContent('/content/Step_9_Guidance.md');
      
      const startTag = `<<${fieldId}-help start>>`;
      const endTag = `<<${fieldId}-help end>>`;
      
      const startIndex = text.indexOf(startTag);
      const endIndex = text.indexOf(endTag);
      
      if (startIndex !== -1 && endIndex !== -1) {
        const helpContent = text.substring(startIndex + startTag.length, endIndex).trim();
        showHelp({
          title: t('step.9.list.help_dialog_title'),
          content: helpContent
        });
      } else {
        showHelp({
          title: t('step.9.list.help_dialog_title'),
          content: t('step.9.list.help_not_found')
        });
      }
    } catch (error) {
      console.error('Error loading help content:', error);
      showHelp({
        title: t('step.9.list.help_dialog_title'),
        content: t('step.9.list.help_error')
      });
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!form) return;
    const target = e.target;
    const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;
    const name = target.name;
    setForm({ ...form, [name]: value });
    // Trigger auto-save
    if (expandedIndex !== null) {
      autoSaveEntry(expandedIndex, { ...form, [name]: value });
    }
  };

  const handleFieldBlur = () => {
    // Do nothing here - form state already updated by handleFormChange
    // Actual saves happen on: periodic (10s), collapse, close, visibility change
  };

  const handleEntryAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!entryInput.trim()) {
      showAlert({
        message: t('step.9.list.dialog.enter_name'),
        variant: 'info'
      });
      return;
    }

    const newPerson: PersonItem = {
      name: entryInput.trim(),
      note: '',
      timing: '',
      form: '',
      amendsPlan: '',
      amendsCompleted: false,
      amendsExperience: ''
    };

    const updatedPersons = [...persons, newPerson];
    // Filter out any undefined/null items and ensure all items are valid objects
    const validData = updatedPersons.filter(item => item != null && typeof item === 'object');
    setPersons(validData);
    setEntryInput('');
    
    // Save immediately
    if (user) {
      InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData).then(() => {
        if (onListUpdate) {
          onListUpdate();
        }
      }).catch(error => {
        console.error('Error saving new entry:', error);
      });
    }
  };

  const handleExpand = async (idx: number) => {
    // If we're expanding a different entry, save the current one first
    if (expandedIndex !== null && expandedIndex !== idx && form) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      await immediateSaveEntry(expandedIndex, form);
    }
    
    // Toggle expansion
    if (expandedIndex === idx) {
      // Collapsing - save before collapsing
      if (form) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        const saved = await immediateSaveEntry(idx, form);
        if (!saved) {
          showAlert({
            message: t('step.9.list.dialog.save_warning'),
            variant: 'warning'
          });
        }
      }
      setExpandedIndex(null);
      setForm(null);
    } else {
      // Expanding - show edit form directly
      const currentData = getCurrentData();
      setExpandedIndex(idx);
      setForm({ ...currentData[idx] });
    }
  };

  const handleDelete = (idx: number) => {
    const person = persons[idx];
    showConfirm({
      message: t('step.9.list.dialog.delete_confirm', { name: person.name }),
      onConfirm: async () => {
        // If deleting the currently expanded entry, save any other changes first
        if (expandedIndex === idx && form) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
        }
        
        const newData = persons.filter((_, i) => i !== idx);
        // Filter out any undefined/null items and ensure all items are valid objects
        const validData = newData.filter(item => item != null && typeof item === 'object');
        setPersons(validData);

        // Save to API
        if (user) {
          try {
            await InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData);
            
            // Notify parent to update counters
            if (onListUpdate) {
              onListUpdate();
            }
            
            // If we deleted the expanded entry, clear expansion
            if (expandedIndex === idx) {
              setExpandedIndex(null);
              setForm(null);
            }
          } catch (error) {
            console.error('Error saving to API:', error);
          }
        }
      },
      variant: 'danger'
    });
  };

  // Handle modal close - save before closing
  const handleClose = async () => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save current entry if expanded
    if (expandedIndex !== null && form) {
      const saved = await immediateSaveEntry(expandedIndex, form);
      
      if (!saved && hasUnsavedChanges) {
        // Save failed - warn user
        setSavingEntry(null);
        showConfirm({
          message: t('step.9.list.dialog.close_unsaved'),
          onConfirm: () => {
            setHasUnsavedChanges(false);
            onClose();
          },
          confirmText: t('step.9.list.dialog.close_anyway'),
          cancelText: t('step.9.list.dialog.try_again'),
          variant: 'danger'
        });
        return;
      }
    }
    
    setExpandedIndex(null);
    setForm(null);
    setHasUnsavedChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('step.9.list.modal_title')}</h2>
          <button className={styles.closeButton} onClick={handleClose} aria-label={t('step.9.list.close')}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          {!user ? (
            <div className={styles.loginPrompt}>
              <p>{t('step.9.list.login_prompt')}</p>
            </div>
          ) : loading ? (
            <div className={styles.loading}>
              <p>{t('step.9.list.loading')}</p>
            </div>
          ) : (
            <>
              <div className={styles.description}>
                <p>{t('step.9.list.description')}</p>
              </div>

              {/* Copy from Step 8 button */}
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCopyFromStep8}
                  style={{
                    background: 'white',
                    color: '#1976d2',
                    border: '2px solid #1976d2',
                    borderRadius: 4,
                    padding: '8px 16px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  title={t('step.9.list.copy_from_step8_title')}
                >
                  {t('step.9.list.copy_from_step8')}
                </button>
              </div>

              {/* Add new person */}
              <form className={styles.quickForm} onSubmit={handleEntryAdd}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('step.9.list.name_label')}
                  </label>
                  <div className={styles.quickInputGroup}>
                    <input
                      type="text"
                      value={entryInput}
                      onChange={e => setEntryInput(e.target.value)}
                      placeholder={t('step.9.list.name_placeholder')}
                      required
                      className={styles.quickInput}
                    />
                    <button type="submit" className={styles.btnSuccess}>{t('step.9.list.add_to_list')}</button>
                  </div>
                </div>
              </form>

              {/* List of persons */}
              {persons.length === 0 ? (
                <div className={styles.emptyList}>
                  <p>{t('step.9.list.empty')}</p>
                </div>
              ) : (
                <div className={styles.inventoryList}>
                  {persons.length > 0 && (
                    <h4 className={styles.inventoryTitle}>
                      {t('step.9.list.your_list')}
                      <span className={styles.inventoryCount}>
                        {t('step.9.list.counts', {
                          total: persons.length,
                          planned: persons.filter(p => hasAmendsPlan(p)).length,
                          completed: persons.filter(p => isPersonCompleted(p)).length,
                        })}
                      </span>
                    </h4>
                  )}
                  <ul className={styles.inventoryItems}>
                    {persons.map((person, idx) => {
                      const isExpanded = expandedIndex === idx;
                      const completed = isPersonCompleted(person);
                      
                      return (
                        <li key={idx} className={`${styles.inventoryItem} ${completed ? styles.completeEntry : styles.incompleteEntry}`}>
                          <div className={styles.itemSummary} onClick={() => handleExpand(idx)}>
                            <div className={styles.itemText}>
                              <span className={styles.expandIcon}>{isExpanded ? '▲' : '▼'}</span>
                              <span className={styles.itemLabel}>{person.name}</span>
                              {savingEntry === idx && (
                                <span className={styles.savingText}>{t('step.workspace.saving')}</span>
                              )}
                              {savedEntry === idx && (
                                <span className={styles.savedText}>{t('step.workspace.saved')}</span>
                              )}
                              {saveError === idx && (
                                <span className={styles.saveErrorText}>{t('step.9.list.save_failed_badge')}</span>
                              )}
                              {savingEntry !== idx && savedEntry !== idx && saveError !== idx && (
                                <>
                                  {completed && (
                                    <span className={styles.completeIcon}>✓</span>
                                  )}
                                  {!completed && (
                                    <span className={styles.incompleteText}>{t('step.9.list.click_for_details')}</span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className={styles.itemControls}>
                              <button 
                                onClick={e => { e.stopPropagation(); handleDelete(idx); }} 
                                className={styles.deleteButton}
                                title={t('step.9.list.delete')}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          
                          {/* Edit Form (inline, shown directly when expanded) */}
                          {isExpanded && form && (
                            <div className={styles.inventoryForm}>
                              <div className={styles.formGroup}>
                                <label>
                                  {t('step.9.list.name_label')}
                                </label>
                                <input 
                                  name="name" 
                                  placeholder={t('step.9.list.name_placeholder')} 
                                  value={form.name || ''} 
                                  onChange={handleFormChange}
                                  onBlur={handleFieldBlur}
                                  required 
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>
                                  {t('step.9.list.note_label')}
                                </label>
                                <textarea 
                                  name="note" 
                                  placeholder={t('step.9.list.note_placeholder')} 
                                  value={form.note || ''} 
                                  onChange={handleFormChange}
                                  onBlur={handleFieldBlur}
                                  rows={2} 
                                />
                              </div>
                              <div className={styles.formRowCompact}>
                                <div className={styles.formGroup}>
                                  <label>
                                    {t('step.9.list.timing_label')}
                                  </label>
                                  <select
                                    name="timing"
                                    value={form.timing || ''}
                                    onChange={handleFormChange}
                                    onBlur={handleFieldBlur}
                                    className={styles.selectInput}
                                  >
                                    <option value="">{t('step.9.list.select_placeholder')}</option>
                                    {TIMING_OPTIONS.map(option => (
                                      <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className={styles.formGroup}>
                                  <label>
                                    {t('step.9.list.form_label')}
                                  </label>
                                  <select
                                    name="form"
                                    value={form.form || ''}
                                    onChange={handleFormChange}
                                    onBlur={handleFieldBlur}
                                    className={styles.selectInput}
                                  >
                                    <option value="">{t('step.9.list.select_placeholder')}</option>
                                    {FORM_OPTIONS.map(option => (
                                      <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className={styles.formGroup}>
                                <label>
                                  {t('step.9.list.amends_plan_label')}
                                  <button 
                                    type="button" 
                                    className={styles.helpBtn} 
                                    onClick={() => openHelp('step9-amends-plan')}
                                    title={t('step.9.list.help_field_title')}
                                    tabIndex={-1}
                                  >
                                    ?
                                  </button>
                                </label>
                                <textarea 
                                  name="amendsPlan" 
                                  placeholder={t('step.9.list.amends_plan_placeholder')} 
                                  value={form.amendsPlan || ''} 
                                  onChange={handleFormChange}
                                  onBlur={handleFieldBlur}
                                  rows={4} 
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '6px', 
                                  marginBottom: '4px',
                                  width: '100%',
                                  justifyContent: 'flex-start'
                                }}>
                                  <input
                                    type="checkbox"
                                    name="amendsCompleted"
                                    checked={form.amendsCompleted || false}
                                    onChange={handleFormChange}
                                    onBlur={handleFieldBlur}
                                    style={{ 
                                      margin: 0, 
                                      padding: 0, 
                                      flexShrink: 0,
                                      width: 'auto',
                                      height: 'auto'
                                    }}
                                  />
                                  <span style={{ 
                                    margin: 0, 
                                    padding: 0,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    fontWeight: 600,
                                    color: '#374151',
                                    fontSize: '14px'
                                  }}>{t('step.9.list.amends_completed_label')}</span>
                                  <button 
                                    type="button" 
                                    className={styles.helpBtn} 
                                    onClick={() => openHelp('step9-amends-completed')}
                                    title={t('step.9.list.help_field_title')}
                                    tabIndex={-1}
                                    style={{ 
                                      margin: 0, 
                                      padding: 0, 
                                      flexShrink: 0
                                    }}
                                  >
                                    ?
                                  </button>
                                </div>
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '0' }}>
                                  {t('step.9.list.amends_completed_hint')}
                                </p>
                              </div>
                              <div className={styles.formGroup}>
                                <label>
                                  {t('step.9.list.amends_experience_label')}
                                  <button 
                                    type="button" 
                                    className={styles.helpBtn} 
                                    onClick={() => openHelp('step9-amends-experience')}
                                    title={t('step.9.list.help_field_title')}
                                    tabIndex={-1}
                                  >
                                    ?
                                  </button>
                                </label>
                                <textarea 
                                  name="amendsExperience" 
                                  placeholder={t('step.9.list.amends_experience_placeholder')} 
                                  value={form.amendsExperience || ''} 
                                  onChange={handleFormChange}
                                  onBlur={handleFieldBlur}
                                  rows={4} 
                                />
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step9ListModal;

